import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ScrollText,
  Search,
  X,
  ChevronRight,
  User,
  Building2,
  Clock,
  Globe,
} from 'lucide-react';
import { auditService } from '@services/audit.service';
import type { AuditLog } from '@apptypes/index';
import { cn } from '@lib/utils';
import { PageGuard } from '@components/PageGuard';

// ── Helpers ─────────────────────────────────────────────────────────────────

const actionChip = (action: string): string => {
  const a = action.toLowerCase();
  if (a.includes('delete') || a.includes('reject') || a.includes('suspend') || a.includes('archive') || a.includes('fail')) {
    return 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]';
  }
  if (a.includes('approve') || a.includes('create') || a.includes('verify') || a.includes('activate') || a.includes('restore') || a.includes('upgrade')) {
    return 'bg-[var(--state-success-bg)] text-[var(--state-success)]';
  }
  if (a.includes('update') || a.includes('edit') || a.includes('assign')) {
    return 'bg-[var(--bg-elevated)] text-[var(--text-accent)]';
  }
  return 'bg-[var(--bg-base)] text-[var(--text-muted)]';
};

const fullName = (log: AuditLog) =>
  [log.userFirstName, log.userLastName].filter(Boolean).join(' ') ||
  log.userEmail ||
  (log.userId ? log.userId.slice(0, 8) : '—');

// Strip the ISO seconds to YYYY-MM-DDTHH:mm for <input type="datetime-local"/>
const toInputDate = (iso?: string) => (iso ? iso.slice(0, 16) : '');
// Convert local input value back to a UTC ISO string the API can parse.
const fromInputDate = (v: string) => (v ? new Date(v).toISOString() : undefined);

// ── Detail modal ────────────────────────────────────────────────────────────

function AuditDetailModal({ log, onClose }: { log: AuditLog; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-lift w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-8 pt-7 pb-5 border-b border-[var(--border-subtle)]">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              Audit entry
            </p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className={cn(
                  'rounded-full px-2.5 py-1 text-[12px] font-semibold',
                  actionChip(log.action),
                )}
              >
                {log.action}
              </span>
              <span className="text-[14px] text-[var(--text-muted)]">on</span>
              <span className="text-[14px] font-medium text-[var(--text-primary)] capitalize">
                {log.entityType}
              </span>
              {log.entityId && (
                <span className="text-[12px] font-mono text-[var(--text-muted)]">
                  {log.entityId.slice(0, 8)}…
                </span>
              )}
            </div>
            {log.description && (
              <p className="text-[13px] text-[var(--text-muted)] mt-2">{log.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
          {/* Meta grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <MetaRow
              icon={<User className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Actor"
              value={fullName(log)}
              hint={log.userEmail ?? undefined}
            />
            <MetaRow
              icon={<Clock className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Timestamp"
              value={new Date(log.createdAt).toLocaleString()}
              hint={log.createdAt}
            />
            <MetaRow
              icon={<Building2 className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Business"
              value={log.businessName ?? '—'}
              hint={log.branchName ?? undefined}
            />
            <MetaRow
              icon={<Globe className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />}
              label="Request"
              value={
                (log.requestMetadata as { ip?: string } | undefined)?.ip ??
                (log.requestMetadata as { ipAddress?: string } | undefined)?.ipAddress ??
                '—'
              }
              hint={
                (log.requestMetadata as { user_agent?: string } | undefined)?.user_agent ??
                (log.requestMetadata as { userAgent?: string } | undefined)?.userAgent ??
                undefined
              }
            />
          </div>

          {/* Changes diff */}
          <ChangesView changes={log.changes} />

          {/* Raw metadata */}
          {log.requestMetadata && Object.keys(log.requestMetadata).length > 0 && (
            <details className="rounded-2xl bg-[var(--bg-base)] p-4">
              <summary className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] cursor-pointer select-none">
                Request metadata
              </summary>
              <pre className="text-[12px] font-mono text-[var(--text-primary)] mt-3 overflow-x-auto whitespace-pre-wrap break-all">
                {JSON.stringify(log.requestMetadata, null, 2)}
              </pre>
            </details>
          )}

          {/* Entity link */}
          {log.entityId && (
            <EntityLink entityType={log.entityType} entityId={log.entityId} />
          )}
        </div>
      </div>
    </div>
  );
}

function MetaRow({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-[var(--border-subtle)] px-4 py-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
        {icon}
        {label}
      </div>
      <div className="text-[14px] text-[var(--text-primary)] mt-1 truncate">{value}</div>
      {hint && <div className="text-[12px] text-[var(--text-muted)] truncate">{hint}</div>}
    </div>
  );
}

function ChangesView({ changes }: { changes?: Record<string, unknown> | null }) {
  if (!changes || Object.keys(changes).length === 0) {
    return (
      <div className="rounded-2xl bg-[var(--bg-base)] p-4 text-[13px] text-[var(--text-muted)]">
        No field-level changes recorded.
      </div>
    );
  }

  // Heuristic: if changes look like { field: { from, to } } or { before, after }, render as diff.
  const before = (changes as Record<string, unknown>)['before'];
  const after = (changes as Record<string, unknown>)['after'];
  if (before && after && typeof before === 'object' && typeof after === 'object') {
    return <DiffTable before={before as Record<string, unknown>} after={after as Record<string, unknown>} />;
  }

  // { field: { from, to } } pattern
  const entries = Object.entries(changes);
  const looksLikeFromTo = entries.every(
    ([, v]) =>
      v != null &&
      typeof v === 'object' &&
      ('from' in (v as object) || 'to' in (v as object)),
  );
  if (looksLikeFromTo) {
    return (
      <div className="rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
        <div className="px-4 py-2 bg-[var(--bg-base)] text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
          Field changes
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              <th className="text-left px-4 py-2">Field</th>
              <th className="text-left px-4 py-2">Before</th>
              <th className="text-left px-4 py-2">After</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-subtle)]">
            {entries.map(([field, v]) => {
              const obj = v as { from?: unknown; to?: unknown };
              return (
                <tr key={field}>
                  <td className="px-4 py-2 text-[13px] font-mono text-[var(--text-primary)]">{field}</td>
                  <td className="px-4 py-2 text-[13px] text-[var(--text-muted)] font-mono">
                    {stringify(obj.from)}
                  </td>
                  <td className="px-4 py-2 text-[13px] text-[var(--text-primary)] font-mono">
                    {stringify(obj.to)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Fallback: raw JSON
  return (
    <div className="rounded-2xl bg-[var(--bg-base)] p-4">
      <div className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-2">
        Changes
      </div>
      <pre className="text-[12px] font-mono text-[var(--text-primary)] overflow-x-auto whitespace-pre-wrap break-all">
        {JSON.stringify(changes, null, 2)}
      </pre>
    </div>
  );
}

function DiffTable({
  before,
  after,
}: {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}) {
  const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();
  return (
    <div className="rounded-2xl border border-[var(--border-subtle)] overflow-hidden">
      <div className="px-4 py-2 bg-[var(--bg-base)] text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
        Diff
      </div>
      <table className="w-full">
        <thead>
          <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
            <th className="text-left px-4 py-2">Field</th>
            <th className="text-left px-4 py-2">Before</th>
            <th className="text-left px-4 py-2">After</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border-subtle)]">
          {fields.map((f) => {
            const b = stringify(before[f]);
            const a = stringify(after[f]);
            const changed = b !== a;
            return (
              <tr key={f} className={changed ? 'bg-[var(--state-warning-bg)]' : ''}>
                <td className="px-4 py-2 text-[13px] font-mono text-[var(--text-primary)]">{f}</td>
                <td className="px-4 py-2 text-[13px] text-[var(--text-muted)] font-mono">{b}</td>
                <td className="px-4 py-2 text-[13px] text-[var(--text-primary)] font-mono">{a}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function EntityLink({ entityType, entityId }: { entityType: string; entityId: string }) {
  const map: Record<string, string> = {
    business: '/businesses',
    businesses: '/businesses',
    order: '/orders',
    orders: '/orders',
    rider: '/riders',
    riders: '/riders',
    user: '/users',
    users: '/users',
  };
  const base = map[entityType.toLowerCase()];
  if (!base) return null;
  return (
    <Link
      to={`${base}/${entityId}`}
      className="inline-flex items-center gap-1 text-[13px] text-[var(--text-primary)] underline decoration-[var(--border-default)] hover:decoration-[var(--text-primary)]"
    >
      Open {entityType} <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
    </Link>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export const AuditLogsPage = () => {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState<AuditLog | null>(null);

  const { data: facets } = useQuery({
    queryKey: ['audit-facets'],
    queryFn: () => auditService.facets(),
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-logs', page, action, entityType, search, dateFrom, dateTo],
    queryFn: () =>
      auditService.list({
        page,
        limit: 50,
        action: action || undefined,
        entityType: entityType || undefined,
        search: search.trim() || undefined,
        dateFrom: fromInputDate(dateFrom),
        dateTo: fromInputDate(dateTo),
      }),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));

  const resetFilters = () => {
    setAction('');
    setEntityType('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <PageGuard permission="audit.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Platform
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Audit Logs
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            Append-only trail of every admin action. Default window: last 30 days.
          </p>
        </div>

        {/* Filters */}
        <div className="rounded-3xl bg-white shadow-soft p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-2">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]"
              strokeWidth={1.5}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search action or description..."
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white pl-10 pr-4 py-2.5 text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)]"
            />
          </div>
          <select
            value={action}
            onChange={(e) => {
              setAction(e.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-3 py-2.5 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-strong)]"
          >
            <option value="">All actions</option>
            {facets?.actions?.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <select
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-3 py-2.5 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-strong)]"
          >
            <option value="">All entities</option>
            {facets?.entityTypes?.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={toInputDate(dateFrom)}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-3 py-2.5 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-strong)]"
            title="From"
          />
          <input
            type="datetime-local"
            value={toInputDate(dateTo)}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-[var(--border-default)] bg-white px-3 py-2.5 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-strong)]"
            title="To"
          />
          {(action || entityType || search || dateFrom || dateTo) && (
            <button
              onClick={resetFilters}
              className="lg:col-span-6 justify-self-end inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-white px-4 py-2 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)]"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          {isLoading && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              Loading audit logs...
            </div>
          )}
          {error && (
            <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
              Failed to load audit logs.
            </div>
          )}

          {!isLoading && !error && (
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    When
                  </th>
                  <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Actor
                  </th>
                  <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Action
                  </th>
                  <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Entity
                  </th>
                  <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Business
                  </th>
                  <th className="text-left px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Description
                  </th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {rows.map((log) => (
                  <tr
                    key={log.id}
                    onClick={() => setSelected(log)}
                    className="hover:bg-[var(--bg-base)] transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-3 text-[13px] text-[var(--text-primary)]">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-3 text-[13px] text-[var(--text-primary)]">
                      <div>{fullName(log)}</div>
                      {log.userEmail && (
                        <div className="text-[12px] text-[var(--text-muted)]">{log.userEmail}</div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          actionChip(log.action),
                        )}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-[13px] text-[var(--text-primary)] capitalize">{log.entityType}</div>
                      {log.entityId && (
                        <div className="text-[12px] font-mono text-[var(--text-muted)]">
                          {log.entityId.slice(0, 8)}…
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-[13px] text-[var(--text-primary)]">
                      {log.businessName || '—'}
                      {log.branchName && (
                        <div className="text-[12px] text-[var(--text-muted)]">{log.branchName}</div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-[13px] text-[var(--text-muted)] max-w-md truncate">
                      {log.description || '—'}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-[var(--text-muted)] inline" strokeWidth={1.5} />
                    </td>
                  </tr>
                ))}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-[14px] text-[var(--text-muted)]">
                      <ScrollText className="w-10 h-10 text-[var(--text-muted)] mx-auto mb-3" strokeWidth={1.5} />
                      No audit logs match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-[var(--border-subtle)]">
              <span className="text-[12px] text-[var(--text-muted)]">
                Page {page} / {totalPages} · {total} total
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-full border border-[var(--border-default)] bg-white px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-full border border-[var(--border-default)] bg-white px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && <AuditDetailModal log={selected} onClose={() => setSelected(null)} />}
    </PageGuard>
  );
};
